// import components
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Icon } from '@sdb/planning-ui';
import VirtualizedAutocomplete from 'components/shared/Autocomplete/VirtualizedAutocomplete';

// import hooks
import { usePlanUnitBranch } from '../../PlanUnitSelector.hooks';

// import utils
import { isValidPlanUnit } from '../../util';

// import types
import type { FC } from 'react';
import type { PlanUnitSelectorSingleProps } from '../../PlanUnitSelector.types';

// import styles
import { StyledCustomTextField } from '../../PlanUnitSelector.styles';

const PlanUnitSelectorSingle: FC<PlanUnitSelectorSingleProps> = ({
  options,
  selectedOptions,
  onChange,
  compareInterval,
  isSingleSelect,
  onAddMore,
  size,
  fullWidth,
  disabled,
  error,
  helperText,
  variant,
  isToolbar,
  onClear
}) => {
  'use memo';

  const {
    t,
    hasKidsPlan,
    getOptionLabel,
    filterOptions,
    renderOption,
    extraWidth
  } = usePlanUnitBranch({ compareInterval });

  const renderInput = (params) => {
    const selectedOption = selectedOptions[0];
    const isGroup = selectedOption && 'costCenters' in selectedOption;

    if (
      selectedOption &&
      ('costCenters' in selectedOption ||
        (hasKidsPlan && selectedOption.isKids))
    ) {
      params.InputProps.startAdornment = (
        <InputAdornment position="start">
          <Icon
            type={isGroup ? 'folder' : 'children'}
            fontSize="small"
            color="inherit"
          />
        </InputAdornment>
      );
    }

    if (!isSingleSelect && !disabled) {
      params.InputProps.className += ' input-box';
      params.InputProps.endAdornment = (
        <div>
          <IconButton
            size="small"
            className="add-more"
            title={t('addMore')}
            onClick={onAddMore}
          >
            <Icon type="plus" fontSize="small" />
          </IconButton>
          {params.InputProps.endAdornment.props.children}
        </div>
      );
    }

    if (
      selectedOption &&
      !isGroup &&
      !isValidPlanUnit(selectedOption.validity, compareInterval)
    ) {
      params.inputProps.value = `${selectedOption.name} (${t(
        'invalid'
      ).toLowerCase()})`;
      params.InputProps.className += ' invalid';
    }

    return (
      <StyledCustomTextField
        label={t('planUnits')}
        error={error}
        variant={variant}
        hiddenLabel={isToolbar}
        helperText={helperText}
        isToolbar={isToolbar}
        size="small"
        {...params}
      />
    );
  };

  return (
    <VirtualizedAutocomplete
      autoHighlight
      extraWidth={extraWidth}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => a.uniqId === b.uniqId}
      onChange={onChange}
      options={options}
      renderInput={renderInput}
      renderOption={renderOption}
      value={selectedOptions[0] || null}
      onInputChange={(event, value, reason) => {
        reason === 'clear' && onClear && onClear();
      }}
      fullWidth={fullWidth}
      disabled={disabled}
      size={size}
    />
  );
};

export default PlanUnitSelectorSingle;
